import { useState, type ReactNode } from 'react'

import { AccordionContent, AccordionItem, AccordionTrigger } from '@components/atoms/Accordion'
import cn from '@components/utils/cn'
import { SegmentedControl } from '@components/atoms/SegmentedControl'
import { useMediaQuery } from '@hooks/useMediaQuery'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('embeds.editor')

const SIDE_BY_SIDE_FROM = '(min-width: 1280px)'

type Pane = 'settings' | 'preview'

type EmbedWorkspaceProps = {
  toolbar?: ReactNode
  settings: ReactNode
  preview: ReactNode
}

export function EmbedWorkspace({ toolbar, settings, preview }: EmbedWorkspaceProps) {
  const roomForBoth = useMediaQuery(SIDE_BY_SIDE_FROM)
  const [pane, setPane] = useState<Pane>('settings')

  return (
    <div className="tw:flex tw:flex-col tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white">
      {(toolbar || !roomForBoth) && (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:border-b tw:border-gray-cool-200 tw:px-6 tw:py-3">
          {toolbar}
          {!roomForBoth && (
            <SegmentedControl<Pane>
              className="tw:ml-auto"
              ariaLabel={t('view')}
              value={pane}
              onChange={setPane}
              options={[
                { value: 'settings', label: t('settings') },
                { value: 'preview', label: t('preview') },
              ]}
            />
          )}
        </div>
      )}
      <div className="tw:flex tw:items-stretch">
        <div
          hidden={!roomForBoth && pane !== 'settings'}
          className={cn(
            'tw:min-w-0 tw:flex-col',
            roomForBoth ? 'tw:flex tw:w-[420px] tw:shrink-0 tw:border-r tw:border-gray-cool-200' : 'tw:flex-1'
          )}
        >
          {settings}
        </div>
        <div
          hidden={!roomForBoth && pane !== 'preview'}
          className={cn(
            'tw:min-w-0 tw:flex-1',
            roomForBoth &&
              'tw:sticky tw:top-(--sb-banner-height,0px) tw:max-h-[calc(100dvh-var(--sb-banner-height,0px)-var(--sb-footer-height,0px))] tw:self-start tw:overflow-y-auto'
          )}
        >
          {preview}
        </div>
      </div>
    </div>
  )
}

export function SettingsSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="tw:flex tw:flex-col tw:gap-4 tw:border-b tw:border-gray-cool-200 tw:px-5 tw:py-5 tw:last:border-b-0">
      <h2 className="tw:m-0 tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-true-500">{title}</h2>
      {children}
    </section>
  )
}

export function SettingsAccordionSection({
  value,
  title,
  children,
}: {
  value: string
  title: string
  children: ReactNode
}) {
  return (
    <AccordionItem value={value} className="tw:border-gray-cool-200 tw:last:border-b-0">
      <AccordionTrigger className="tw:px-5 tw:py-4 tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-true-500 tw:hover:no-underline">
        {title}
      </AccordionTrigger>
      <AccordionContent className="tw:flex tw:flex-col tw:gap-4 tw:px-5 tw:pb-5">{children}</AccordionContent>
    </AccordionItem>
  )
}
